import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { listQuizAttempts, getQuiz, getAttemptResults } from '../services/quizService';

const QuizzesAttempts: React.FC = () => {
  const { user } = useAuth();
  const [attempts, setAttempts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttempt, setSelectedAttempt] = useState<any | null>(null);
  const [attemptDetails, setAttemptDetails] = useState<any | null>(null);
  const [quizTitles, setQuizTitles] = useState<{ [quizId: number]: string }>({});

  useEffect(() => {
    async function fetchAttempts() {
      if (!user?.id) return;
      setLoading(true);
      setError(null);
      try {
        const data = await listQuizAttempts(user.id);
        setAttempts(data as any[]);
        // Fetch quiz titles for each attempt
        const titles: { [quizId: number]: string } = {};
        await Promise.all(
          (data as any[]).map(async (a: any) => {
            if (!titles[a.quiz_id]) {
              const quiz = await getQuiz(a.quiz_id);
              titles[a.quiz_id] = quiz.title;
            }
          })
        );
        setQuizTitles(titles);
      } catch (e: any) {
        setError(e?.response?.data?.detail || 'Failed to load attempts');
      } finally {
        setLoading(false);
      }
    }
    fetchAttempts();
  }, [user?.id]);

  const handleViewDetails = async (attempt: any) => {
    setSelectedAttempt(attempt);
    setAttemptDetails(null);
    try {
      const details = await getAttemptResults(attempt.id);
      setAttemptDetails(details);
    } catch (e: any) {
      setAttemptDetails({ error: e?.response?.data?.detail || 'Failed to load details' });
    }
  };

  return (
    <div>
      <h1>My Quiz Attempts</h1>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p style={{ color: 'red' }}>{error}</p>
      ) : attempts.length === 0 ? (
        <p>No quiz attempts found.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>Quiz</th>
              <th>Score</th>
              <th>Status</th>
              <th>Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {attempts.map((a) => (
              <tr key={a.id}>
                <td>{quizTitles[a.quiz_id] || a.quiz_id}</td>
                <td>{a.score !== null && a.score !== undefined ? a.score : '-'}</td>
                <td>{a.is_submitted ? 'Submitted' : 'In Progress'}</td>
                <td>{a.completed_at ? new Date(a.completed_at).toLocaleString() : new Date(a.started_at).toLocaleString()}</td>
                <td>
                  <button onClick={() => handleViewDetails(a)}>View Details</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Attempt Details Dialog */}
      {selectedAttempt && (
        <div style={{ position: 'fixed', top: 40, left: 0, right: 0, margin: 'auto', maxWidth: 600, background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: 24, zIndex: 1000 }}>
          <h2>Quiz Attempt Details</h2>
          <button style={{ float: 'right' }} onClick={() => { setSelectedAttempt(null); setAttemptDetails(null); }}>Close</button>
          {!attemptDetails ? (
            <p>Loading...</p>
          ) : attemptDetails.error ? (
            <p style={{ color: 'red' }}>{attemptDetails.error}</p>
          ) : (
            <div>
              <p><b>Quiz:</b> {quizTitles[selectedAttempt.quiz_id] || selectedAttempt.quiz_id}</p>
              <p><b>Score:</b> {attemptDetails.score}</p>
              <p><b>Status:</b> {selectedAttempt.is_submitted ? 'Submitted' : 'In Progress'}</p>
              <p><b>Completed At:</b> {selectedAttempt.completed_at ? new Date(selectedAttempt.completed_at).toLocaleString() : '-'}</p>
              <h3>Responses</h3>
              <ul>
                {attemptDetails.responses && attemptDetails.responses.length > 0 ? attemptDetails.responses.map((resp: any, idx: number) => (
                  <li key={resp.id} style={{ color: resp.is_correct === true ? 'green' : resp.is_correct === false ? 'red' : '#888' }}>
                    Q{idx + 1}: {resp.question_id} — {resp.is_correct === true ? 'Correct' : resp.is_correct === false ? 'Incorrect' : 'Pending Review'}
                    {resp.answer_text && <span> (Answer: {resp.answer_text})</span>}
                    {resp.selected_option_id && <span> (Option: {resp.selected_option_id})</span>}
                  </li>
                )) : <li>No responses found.</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizzesAttempts; 